import { useEffect, useRef, useState } from "react"
import { IParams } from "../../../context/ListRecordProvider/types"
import { getDashboardReport, getRealTimeTransactionLog, getReport } from "../../../services/reports"
import ActionArea from "../../../components/ActionArea"
import SmartSearch from "../../../components/UI/SmartSearch"
import SwitchInput from "../../../components/UI/SwitchInput"
import ReportParser from "../../../components/ReportParser"
import endpoints from "../../../utils/endpoints"
import List from "../../../components/List"
import { useLoading } from "../../../context/LoadingProvider/useLoading"
import { onlyNumbers } from "../../../utils/formValidation"
import { numberToCurrency } from "../../../utils/objectUtils"
import { Card } from 'antd'
import { valueToCurrency } from "../../../utils/stringUtils"
import { compareNegativeCurrency, compareNumbers, stringCompare } from "../../../utils/sorters"
import dayjs from "dayjs";
import { sendSystemLog } from "../../../context/AuthProvider/useAuth"

const REFRESH_TIME =  10 * 1000

export interface IGameSummary {
  title: string
  form: string
  wager: string
  plays: number
  wagers: string 
  prizes: string 
}

export interface IJackpotSummary {
  status: string
  answer: JackpotSumaryAnswer []
}

export type JackpotSumaryAnswer = {
  id: string,
  template_id: string,
  instance_id: string,
  type: string,
  addition: number,
  name: string,
  house_seed: number,
  contribution_percent: string,
  tags: string[],
  game_ids: string[],
  partial_prize: number,
  current_prize: number,
  players_contribution: number,
  start_timestamp: string,
  start_business_date: string
}

export interface IDashboardReport {
  current_business_day: string,
  daily_game_summary: IGameSummary[],
  daily_jackpot_summary: IJackpotSummary,
  devices_daily_summary: {
    activated_devices: number,
    active_session: number
  },
  location_count: number
}

function Dashboard() {
  const loading = useLoading()
  const [jackpotSummary, setJackpotSummary] = useState<JackpotSumaryAnswer[]>([])
  const [gameSumary, setGameSummary] = useState<IGameSummary[]>([])
  const [infoCard, setInfoCard] = useState({
    devicesActivated: 0,
    devicesPlaying: 0,
    totalLocations: 0,
    currentDay: ''
  })
  const log = {
    log_type: 'visited',
    log_object: 'dashboard'
  }

  useEffect(()=> {
    sendSystemLog(log)
    refreshReport()
  },[])

  const refreshReport = async () => {
    loading.addLoading()
    const res =  await getDashboardReport()
    setJackpotSummary([...res.daily_jackpot_summary.answer, {
      id: '',
      template_id: '',
      instance_id: '',
      type: '--Total--',
      addition: getTotalField('addition', res.daily_jackpot_summary.answer),
      name: '--',
      house_seed: getTotalField('house_seed', res.daily_jackpot_summary.answer),
      contribution_percent:`${getTotalField('contribution_percent', res.daily_jackpot_summary.answer)}`,
      tags: [],
      game_ids: [],
      partial_prize: getTotalField('partial_prize', res.daily_jackpot_summary.answer),
      current_prize: getTotalField('current_prize', res.daily_jackpot_summary.answer),
      players_contribution: getTotalField('players_contribution', res.daily_jackpot_summary.answer),
      start_timestamp: '',
      start_business_date: ''
    }])
    setGameSummary([...res.daily_game_summary, {
      title: '--Total--',
      form: '--',
      wager: '--',
      plays: getTotalField('plays', res.daily_game_summary),
      wagers: `${getTotalField('wagers', res.daily_game_summary)}`,
      prizes: `${getTotalField('prizes', res.daily_game_summary)}`
    }])
    setInfoCard({
      devicesActivated: res.devices_daily_summary.activated_devices,
      devicesPlaying: res.devices_daily_summary.active_session,
      totalLocations: res.location_count,
      currentDay: res.current_business_day
    })
    loading.removeLoading()
  }

  const getTotalField = (path: string, array: any[]) => {
    let total = 0
    array.forEach((item: any) =>  {
      if(!item[path as keyof typeof array]) return
      if(parseInt(item[path as keyof typeof array])) return total += parseInt(item[path as keyof typeof array])
      total += parseInt(onlyNumbers(item[path as keyof typeof array]))
    })
    return total
  }

  const isFistGameSummaryRow = (recordA: IGameSummary, recordB: IGameSummary) => recordA.title === '--Total--' || recordB.title === '--Total--'

  const isFistJackpotSummaryRow = (recordA: JackpotSumaryAnswer, recordB: JackpotSumaryAnswer) => recordA.type === '--Total--' || recordB.type === '--Total--'


  return (
    <div className="report-view-page">
      <div className="dashboard-report__date">
       Current Business Day: {infoCard.currentDay && dayjs(infoCard.currentDay, 'YYYY-MM-DD').format('MMMM DD, YYYY')}
      </div>
      <div className="dashboard-report__card">
        <Card title="Device Session" bordered={false} style={{ width: 300 }}>
          <p>Unique devices activated today : {infoCard.devicesActivated}</p>
          <p>Devices playing now : {infoCard.devicesPlaying}</p>
        </Card>
        <Card title="Location Count" bordered={false} style={{ width: 300 }}>
          <p>Total locations : {infoCard.totalLocations}</p>
        </Card>
      </div>
      <div className="dashboard-report__list">
        <h3>Today's Game Summary</h3>
        <List
          rows={gameSumary} 
          pagination={false}
          columns={[
            {
              title: 'Title',
              dataIndex: 'title',
              key: 'title',
              render: (value: string) => value,
              sorter: (a: IGameSummary, b: IGameSummary) => stringCompare(a.title, b.title, isFistGameSummaryRow(a, b)),
            },
            {
              title: 'Form',
              dataIndex: 'form',
              key: 'form',
              render: (value: string) => value,
              sorter: (a: IGameSummary, b: IGameSummary) => stringCompare(a.form, b.form, isFistGameSummaryRow(a, b)),
            },
            {
              title: 'Wager',
              dataIndex: 'wager',
              key: 'wager',
              render: (value: string, record: IGameSummary, index: number) =>  index === gameSumary.length -1 ? value : valueToCurrency(value),
              sorter: (a: IGameSummary, b: IGameSummary) => compareNegativeCurrency(a.wager, b.wager, isFistGameSummaryRow(a, b)),
            },
            {
              title: 'Plays',
              dataIndex: 'plays',
              key: 'plays',
              render: (value: string) => value,
              sorter: (a: IGameSummary, b: IGameSummary) => compareNumbers(a.plays, b.plays, isFistGameSummaryRow(a, b)),
            },
            {
              title: 'Wagers',
              dataIndex: 'wagers',
              key: 'wagers',
              render: (value: string) => valueToCurrency(value),
              sorter: (a: IGameSummary, b: IGameSummary) => compareNegativeCurrency(a.wagers, b.wagers, isFistGameSummaryRow(a, b)),
            },
            {
              title: 'Prizes',
              dataIndex: 'prizes',
              key: 'prizes',
              render: (value: string) => valueToCurrency(value),
              sorter: (a: IGameSummary, b: IGameSummary) => compareNegativeCurrency(a.prizes, b.prizes, isFistGameSummaryRow(a, b)),
            },
          ]}
        />
      </div>
      <div className="dashboard-report__list">
        <h3>Today's Jackpot Summary</h3>
        <List
          rows={jackpotSummary}
          pagination={false}
          columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (value: string) => value,
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => stringCompare(a.type, b.type, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
              render: (value: string) => value,
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => stringCompare(a.name, b.name, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'House seed',
              dataIndex: 'house_seed',
              key: 'house_seed',
              render: (value: number) => valueToCurrency(value),
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => compareNegativeCurrency(`${a.house_seed}`, `${b.house_seed}`, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'Addition',
              dataIndex: 'addition',
              key: 'addition',
              render: (value: string) =>  valueToCurrency(value),
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => compareNegativeCurrency(`${a.addition}`, `${b.addition}`, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'Contribution',
              dataIndex: 'players_contribution',
              key: 'players_contribution',
              render: (value: string) =>  valueToCurrency(value),
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => compareNegativeCurrency(`${a.players_contribution}`, `${b.players_contribution}`, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'Partial Prize',
              dataIndex: 'partial_prize',
              key: 'partial_prize',
              render: (value: string) =>  valueToCurrency(value),
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => compareNegativeCurrency(`${a.partial_prize}`, `${b.partial_prize}`, isFistJackpotSummaryRow(a, b)),
            },
            {
              title: 'Jackpot Prize',
              dataIndex: 'current_prize',
              key: 'current_prize',
              render: (value: string) =>  valueToCurrency(value),
              sorter: (a: JackpotSumaryAnswer, b: JackpotSumaryAnswer) => compareNegativeCurrency(`${a.current_prize}`, `${b.current_prize}`, isFistJackpotSummaryRow(a, b)),
            },
          ]}
        />
      </div>

    </div>
  )
}

export default Dashboard